import { useAuth } from '@clerk/clerk-react';

import { TGD_ROLES } from '@/lib/auth/tgd-roles';

/**
 * Read the custom Clerk session-token claims synchronously — no network call.
 *
 * Source: Clerk Dashboard → Sessions → Customize session token (see
 * apps/crm-app/src/types/clerk-session-claims.d.ts for the shape).
 *
 * Prefer this over `trpc.users.getCurrentUserInfo` for role/scope checks:
 * it removes a round-trip per page load and returns instantly once Clerk
 * has loaded the session.
 */
export interface SessionClaimsView {
  isLoaded: boolean;
  isSignedIn: boolean;
  tgdRoles: string[];
  /** Partner scope (null = global tagada staff). */
  whitelabelScope: string | null;
  /** The whitelabel tag of the currently active org. */
  activeOrgWhitelabel: string | null;

  /**
   * "Platform admin" = `superadmin` OR `founder`. Founder is a strict
   * superset of `superadmin` (May 2026 IAM audit), so any UI gated on
   * "is super" semantically means "is platform admin" — every consumer
   * of `isSuperAdmin` therefore covers founders too. If you need to
   * test for `superadmin` *specifically* (rare), check `tgdRoles`
   * directly.
   */
  isSuperAdmin: boolean;
  isAccountManager: boolean;
  isPartnerAdmin: boolean;
  /**
   * Founder = root-of-trust role. The IAM panel uses this to enable
   * the `superadmin` / `founder` role checkboxes — only a founder is
   * authorised to grant those (see `canAssignTgdRole`'s founder gate).
   * Plain superadmins see the checkboxes greyed out.
   */
  isFounder: boolean;
  /** `rx_admin` — TagadaRx module management within `whitelabelScope`. */
  isRxAdmin: boolean;
  /** `rx_ops` or `rx_admin` (or platform admin) — may work the Rx support desk. */
  isRxStaff: boolean;
  /**
   * True when the user has a partner scope AND that scope does NOT match the
   * active org. Useful to short-circuit UI that only makes sense when staff
   * is acting within their owned scope.
   */
  isScopedOutOfActiveOrg: boolean;
}

export function useSessionClaims(): SessionClaimsView {
  const { sessionClaims, isLoaded, isSignedIn } = useAuth();
  const claims = (sessionClaims ?? {}) as CustomJwtSessionClaims;

  const tgdRoles = Array.isArray(claims.tgdRoles) ? claims.tgdRoles : [];
  const whitelabelScope = typeof claims.whitelabelScope === 'string' ? claims.whitelabelScope : null;
  const activeOrgWhitelabel =
    typeof claims.activeOrgWhitelabel === 'string' ? claims.activeOrgWhitelabel : null;

  const isScopedOutOfActiveOrg =
    !!whitelabelScope && !!activeOrgWhitelabel && whitelabelScope !== activeOrgWhitelabel;
  const isRxAdmin = tgdRoles.includes(TGD_ROLES.RX_ADMIN);

  return {
    isLoaded,
    isSignedIn: !!isSignedIn,
    tgdRoles,
    whitelabelScope,
    activeOrgWhitelabel,
    isSuperAdmin: tgdRoles.includes(TGD_ROLES.SUPERADMIN) || tgdRoles.includes(TGD_ROLES.FOUNDER),
    isAccountManager: tgdRoles.includes(TGD_ROLES.ACCOUNTMANAGER),
    isPartnerAdmin: tgdRoles.includes(TGD_ROLES.PARTNERADMIN),
    isFounder: tgdRoles.includes(TGD_ROLES.FOUNDER),
    isRxAdmin,
    isRxStaff: isRxAdmin || tgdRoles.includes(TGD_ROLES.RX_OPS),
    isScopedOutOfActiveOrg,
  };
}
